// ---------------------------------------------------------------------------
// Tabs Renderer
// ---------------------------------------------------------------------------
// Engagement primitive — publishes the editor `Tabs` block as a static
// list of titled content panels parsed from the pipe-delimited textarea
// the Puck editor uses ("Title | Content" per line). The published
// output is intentionally script-free: each tab becomes a labelled
// `<section>` so the page remains crawlable and accessible. Client-side
// tab switching is layered on by progressive enhancement when the
// `ease-tabs` block-styles loader hydrates the `role="tab"` controls.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

interface TabsProps {
  items?: string;
}

interface TabItem {
  title: string;
  content: string;
}

function parseTabs(value: unknown): TabItem[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [titlePart, contentPart] = line.split("|");
      return {
        title: (titlePart ?? "").trim(),
        content: (contentPart ?? "").trim(),
      };
    })
    .filter((tab) => tab.title.length > 0);
}

function slugFor(title: string, index: number): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return base.length > 0 ? `${base}-${index}` : `tab-${index}`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as TabsProps;

  const tabs = parseTabs(p.items);
  if (tabs.length === 0) {
    return "<!-- tabs missing items -->";
  }

  const tabList = tabs
    .map((tab, index) => {
      const slug = slugFor(tab.title, index);
      const selected = index === 0 ? "true" : "false";
      return `      <button type="button" role="tab" id="tab-${attr(slug)}" aria-controls="panel-${attr(slug)}" aria-selected="${attr(selected)}" class="ease-tabs__tab">${text(tab.title)}</button>`;
    })
    .join("\n");

  const panels = tabs
    .map((tab, index) => {
      const slug = slugFor(tab.title, index);
      const hidden = index === 0 ? "" : ' hidden=""';
      return `    <section role="tabpanel" id="panel-${attr(slug)}" aria-labelledby="tab-${attr(slug)}" class="ease-tabs__panel"${hidden}>
      <p class="ease-tabs__content">${text(tab.content)}</p>
    </section>`;
    })
    .join("\n");

  return `<section class="ease-section ease-tabs">
    <div class="ease-tabs__list" role="tablist">
${tabList}
    </div>
${panels}
</section>`;
}

registerRenderer("tabs", { render });
